import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Grid,
  Menu,
  Segment,
  Header,
  Divider,
  Button,
  Label,
  Table,
  Message,
} from 'semantic-ui-react';

const CodeBlock = ({ code, language = 'bash' }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{
      background: '#282c34',
      borderRadius: '8px',
      padding: '0',
      margin: '16px 0',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    }}>
      <div style={{
        background: '#21252b',
        padding: '8px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #3e4451',
      }}>
        <span style={{ color: '#abb2bf', fontSize: '12px', textTransform: 'uppercase' }}>{language}</span>
        <Button
          size="mini"
          compact
          onClick={handleCopy}
          style={{
            background: copied ? '#2ecc71' : '#4a4f5b',
            color: '#fff',
            border: 'none',
            padding: '4px 12px',
            fontSize: '11px',
          }}
        >
          {copied ? '✓ ' + t('channel_guide.buttons.copied') : t('channel_guide.buttons.copy')}
        </Button>
      </div>
      <pre style={{
        margin: 0,
        padding: '16px',
        color: '#abb2bf',
        fontFamily: 'Fira Code, Monaco, Consolas, monospace',
        fontSize: '13px',
        lineHeight: '1.6',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        overflow: 'auto',
        maxHeight: '500px',
      }}>
        <code>{code}</code>
      </pre>
    </div>
  );
};

const APIDoc = () => {
  const { t, i18n } = useTranslation();
  const [activeSection, setActiveSection] = React.useState('overview');
  const [expandedSections, setExpandedSections] = React.useState(['overview']);
  const baseUrl = window.location.origin;
  const token = 'sk-your-token-here';

  // 切换二级菜单展开/收起
  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => 
      prev.includes(sectionKey) 
        ? prev.filter(s => s !== sectionKey)
        : [...prev, sectionKey]
    );
  };

  // 二级导航菜单结构
  const menuStructure = [
    {
      key: 'overview',
      title: i18n.language === 'zh' ? '快速开始' : 'Quick Start',
      icon: 'rocket',
      children: [
        { key: 'overview', text: i18n.language === 'zh' ? '接口概览' : 'API Overview', icon: 'info circle' },
        { key: 'auth', text: i18n.language === 'zh' ? '认证方式' : 'Authentication', icon: 'lock' },
      ]
    },
    {
      key: 'chat',
      title: i18n.language === 'zh' ? '对话接口' : 'Chat API',
      icon: 'comments',
      children: [
        { key: 'chat-openai', text: i18n.language === 'zh' ? 'OpenAI (GPT)' : 'OpenAI (GPT)', icon: 'comment' },
        { key: 'chat-gemini', text: i18n.language === 'zh' ? 'Gemini' : 'Gemini', icon: 'comment' },
        { key: 'chat-claude', text: i18n.language === 'zh' ? 'Claude' : 'Claude', icon: 'comment' },
        { key: 'chat-ali', text: i18n.language === 'zh' ? '阿里云百炼' : 'Alibaba Cloud Bailian', icon: 'comment' },
      ]
    },
    {
      key: 'image',
      title: i18n.language === 'zh' ? '图片生成' : 'Image Generation',
      icon: 'image',
      children: [
        { key: 'image-junyun', text: i18n.language === 'zh' ? '君云AIGC' : 'Junyun AIGC', icon: 'picture' },
        { key: 'image-ali', text: i18n.language === 'zh' ? '阿里通义万相' : 'Alibaba Tongyi Wanxiang', icon: 'picture' },
        { key: 'image-gpt', text: i18n.language === 'zh' ? 'GPT Image' : 'GPT Image', icon: 'picture' },
      ]
    },
    {
      key: 'video',
      title: i18n.language === 'zh' ? '视频生成' : 'Video Generation',
      icon: 'video',
      children: [
        { key: 'video-kling', text: i18n.language === 'zh' ? '可灵 Kling' : 'Kling', icon: 'film' },
        { key: 'video-seedance', text: i18n.language === 'zh' ? '即梦 Seedance' : 'Seedance', icon: 'film' },
        { key: 'video-hailuo', text: i18n.language === 'zh' ? '海螺 Hailuo' : 'Hailuo', icon: 'film' },
        { key: 'video-vidu', text: i18n.language === 'zh' ? '迹兔 Vidu' : 'Vidu', icon: 'film' },
        { key: 'video-jimeng', text: i18n.language === 'zh' ? '即梦 Jimeng' : 'Jimeng', icon: 'film' },
      ]
    },
    {
      key: '3d',
      title: i18n.language === 'zh' ? '3D生成' : '3D Generation',
      icon: 'cube',
      children: [
        { key: '3d-text', text: i18n.language === 'zh' ? '文生3D' : 'Text to 3D', icon: 'cube' },
        { key: '3d-image', text: i18n.language === 'zh' ? '图生3D' : 'Image to 3D', icon: 'cube' },
        { key: '3d-multi', text: i18n.language === 'zh' ? '多图生3D' : 'Multi-Image to 3D', icon: 'cubes' },
      ]
    },
    {
      key: 'task',
      title: i18n.language === 'zh' ? '任务查询' : 'Task Query',
      icon: 'search',
      children: [
        { key: 'task-status', text: i18n.language === 'zh' ? '查询任务状态' : 'Query Task Status', icon: 'check circle' },
        { key: 'task-poll', text: i18n.language === 'zh' ? '轮询最佳实践' : 'Polling Best Practices', icon: 'refresh' },
      ]
    },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div>
            {/* 引言区域 */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '12px',
              padding: '40px',
              color: '#fff',
              marginBottom: '32px',
              boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
            }}>
              <h1 style={{ 
                margin: '0 0 16px 0', 
                fontSize: '32px',
                fontWeight: '700',
                lineHeight: '1.3',
              }}>
                {i18n.language === 'zh' ? '欢迎使用 AI 聚合平台' : 'Welcome to AI Aggregation Platform'}
              </h1>
              <p style={{ 
                margin: '0 0 24px 0',
                fontSize: '18px',
                lineHeight: '1.8',
                opacity: '0.95',
              }}>
                {i18n.language === 'zh' 
                  ? '一站式 AI 服务接入平台，整合全球顶级 AI 模型与能力。通过统一的 OpenAI 兼容接口，轻松调用 GPT、Gemini、Claude、文心一言等主流大语言模型，以及图像生成、视频生成、3D 生成等 AIGC 能力。'
                  : 'A one-stop AI service integration platform, integrating world-class AI models and capabilities. Through a unified OpenAI-compatible interface, easily access leading LLMs like GPT, Gemini, Claude, and AIGC capabilities including image, video, and 3D generation.'}
              </p>
              <div style={{
                display: 'flex',
                gap: '16px',
                flexWrap: 'wrap',
              }}>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  padding: '12px 20px',
                  backdropFilter: 'blur(10px)',
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>7+</div>
                  <div style={{ fontSize: '13px', opacity: '0.9' }}>
                    {i18n.language === 'zh' ? 'AI 渠道' : 'AI Channels'}
                  </div>
                </div>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  padding: '12px 20px',
                  backdropFilter: 'blur(10px)',
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>100+</div>
                  <div style={{ fontSize: '13px', opacity: '0.9' }}>
                    {i18n.language === 'zh' ? 'AI 模型' : 'AI Models'}
                  </div>
                </div>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  padding: '12px 20px',
                  backdropFilter: 'blur(10px)',
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>99.9%</div>
                  <div style={{ fontSize: '13px', opacity: '0.9' }}>
                    {i18n.language === 'zh' ? '服务可用性' : 'Uptime'}
                  </div>
                </div>
              </div>
            </div>

            {/* 非开发人员提示 */}
            <div style={{
              background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '32px',
              boxShadow: '0 4px 12px rgba(252, 182, 159, 0.3)',
              border: '2px solid #ff9a76',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                <div style={{
                  background: '#fff',
                  borderRadius: '50%',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  flexShrink: 0,
                }}>
                  💡
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ 
                    margin: '0 0 12px 0', 
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#d35400',
                  }}>
                    {i18n.language === 'zh' ? '不是专业开发人员？' : 'Not a Professional Developer?'}
                  </h3>
                  <p style={{ 
                    margin: '0 0 16px 0',
                    fontSize: '16px',
                    lineHeight: '1.8',
                    color: '#666',
                  }}>
                    {i18n.language === 'zh'
                      ? '本页面主要面向专业开发人员，包含详细的 API 接口文档、技术参数和高级用法。如果您是初学者或不熟悉编程，我们建议您查看专门的软件对接教程，那里有更简单易懂的步骤指导。'
                      : 'This page is mainly for professional developers, containing detailed API documentation, technical parameters, and advanced usage. If you are a beginner or unfamiliar with programming, we recommend checking our software integration guides, which provide simpler and easier-to-follow instructions.'}
                  </p>
                  <a href='/software-guide' style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: '#fff',
                    color: '#d35400',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '15px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                  }}
                  >
                    <span style={{ fontSize: '18px' }}>📚</span>
                    {i18n.language === 'zh' ? '前往新手教程（零基础友好）' : 'Go to Beginner Guide (Zero Basics Friendly)'}
                    <span>→</span>
                  </a>
                </div>
              </div>
            </div>

            <Header as='h2' style={{ color: '#1a1a2e', marginBottom: '16px' }}>
              {i18n.language === 'zh' ? '快速开始' : 'Quick Start'}
            </Header>
            <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.8' }}>
              {i18n.language === 'zh' 
                ? 'AI聚合平台提供统一的OpenAI兼容接口，支持多种AI模型和服务。所有接口均基于RESTful API设计，使用JSON格式进行数据交换。'
                : 'AI Platform provides a unified OpenAI-compatible API supporting multiple AI models and services. All APIs are designed based on RESTful principles using JSON format for data exchange.'}
            </p>

            <Segment style={{ marginTop: '24px', background: '#f8f9fa', borderLeft: '4px solid #667eea' }}>
              <Header as='h3' style={{ color: '#667eea' }}>
                <i className="rocket icon" /> {i18n.language === 'zh' ? '三步开始使用' : 'Get Started in 3 Steps'}
              </Header>
              <div style={{ marginTop: '16px' }}>
                <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{
                    background: '#667eea',
                    color: '#fff',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    flexShrink: 0,
                  }}>1</div>
                  <div>
                    <strong>{i18n.language === 'zh' ? '获取 API Key' : 'Get API Key'}</strong>
                    <p style={{ margin: '4px 0 0 0', color: '#666' }}>
                      {i18n.language === 'zh' ? '在令牌页面创建并获取您的 API Key'
                        : 'Create and get your API Key from the Token page'}
                    </p>
                  </div>
                </div>
                <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{
                    background: '#667eea',
                    color: '#fff',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    flexShrink: 0,
                  }}>2</div>
                  <div>
                    <strong>{i18n.language === 'zh' ? '配置请求' : 'Configure Request'}</strong>
                    <p style={{ margin: '4px 0 0 0', color: '#666' }}>
                      {i18n.language === 'zh' ? '设置 API 地址为 ' : 'Set API base URL to '}
                      <code style={{ background: '#e9ecef', padding: '2px 6px', borderRadius: '4px' }}>{baseUrl}/v1</code>
                      {i18n.language === 'zh' ? '，并在 Header 中携带 API Key' : ', and include API Key in Header'}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{
                    background: '#667eea',
                    color: '#fff',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    flexShrink: 0,
                  }}>3</div>
                  <div>
                    <strong>{i18n.language === 'zh' ? '开始调用' : 'Start Using'}</strong>
                    <p style={{ margin: '4px 0 0 0', color: '#666' }}>
                      {i18n.language === 'zh' ? '使用 OpenAI 兼容格式调用各类 AI 模型'
                        : 'Use OpenAI-compatible format to call various AI models'}
                    </p>
                  </div>
                </div>
              </div>
            </Segment>

            <Segment style={{ marginTop: '24px', background: '#f0f9ff', borderLeft: '4px solid #0ea5e9' }}>
              <Header as='h3' style={{ color: '#0ea5e9' }}>
                <i className="info circle icon" /> {i18n.language === 'zh' ? '基础信息' : 'Base Information'}
              </Header>
              <Table compact definition>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell width={3} style={{ fontWeight: 'bold' }}>
                      {i18n.language === 'zh' ? '基础URL' : 'Base URL'}
                    </Table.Cell>
                    <Table.Cell><code style={{ background: '#e9ecef', padding: '2px 6px', borderRadius: '4px' }}>{baseUrl}/v1</code></Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell width={3} style={{ fontWeight: 'bold' }}>
                      {i18n.language === 'zh' ? '认证方式' : 'Authentication'}
                    </Table.Cell>
                    <Table.Cell>Bearer Token (API Key)</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell width={3} style={{ fontWeight: 'bold' }}>
                      {i18n.language === 'zh' ? '数据格式' : 'Data Format'}
                    </Table.Cell>
                    <Table.Cell>application/json</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell width={3} style={{ fontWeight: 'bold' }}>
                      {i18n.language === 'zh' ? '接口协议' : 'Protocol'}
                    </Table.Cell>
                    <Table.Cell>HTTPS / RESTful API</Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </Segment>
          </div>
        );

      case 'auth':
        return (
          <div>
            <Header as='h2' style={{ color: '#1a1a2e' }}>
              {i18n.language === 'zh' ? '认证方式' : 'Authentication'}
            </Header>
            <p style={{ color: '#555', lineHeight: '1.8' }}>
              {i18n.language === 'zh'
                ? '所有API请求都需要在HTTP Header中包含认证信息。使用Bearer Token方式，将您的API Key添加到Authorization头部。'
                : 'All API requests must include authentication information in the HTTP header. Use Bearer Token method by adding your API Key to the Authorization header.'}
            </p>

            <Header as='h3' style={{ marginTop: '24px' }}>
              {i18n.language === 'zh' ? '请求示例' : 'Request Example'}
            </Header>
            <CodeBlock language='bash' code={`curl ${baseUrl}/v1/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${token}" \\
  -d '{
    "model": "gpt-4",
    "messages": [{"role": "user", "content": "Hello!"}]
  }'`} />
          </div>
        );

      case 'chat-openai':
        return (
          <div>
            <Header as='h2' style={{ color: '#1a1a2e', marginBottom: '16px' }}>
              <i className="comment icon" style={{ color: '#667eea' }} />
              {i18n.language === 'zh' ? 'OpenAI (GPT) 对话接口' : 'OpenAI (GPT) Chat API'}
            </Header>
            <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.8', marginBottom: '24px' }}>
              {i18n.language === 'zh' ? '通过 OpenAI 兼容接口调用 GPT-4、GPT-4o、GPT-3.5 等模型。支持对话、视觉理解、函数调用等功能。' : 'Call GPT-4, GPT-4o, GPT-3.5 and other models through OpenAI-compatible interface. Supports chat, vision, function calling and more.'}
            </p>

            <Segment style={{ background: '#f0f9ff', borderLeft: '4px solid #0ea5e9' }}>
              <Header as='h4' style={{ color: '#0ea5e9', marginTop: 0 }}>
                <i className="info circle icon" /> {i18n.language === 'zh' ? '支持模型' : 'Supported Models'}
              </Header>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                {['gpt-4', 'gpt-4-turbo', 'gpt-4o', 'gpt-4o-mini', 'gpt-3.5-turbo', 'gpt-3.5-turbo-16k'].map(model => (
                  <Label key={model} color='blue' size='small'>{model}</Label>
                ))}
              </div>
            </Segment>

            <Header as='h3' style={{ marginTop: '24px' }}>{i18n.language === 'zh' ? '请求示例' : 'Request Example'}</Header>
            <CodeBlock language='bash' code={`curl ${baseUrl}/v1/chat/completions \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "gpt-4o",
    "messages": [
      {"role": "system", "content": "You are a helpful assistant"},
      {"role": "user", "content": "Hello!"}
    ],
    "max_tokens": 1000,
    "temperature": 0.7
  }'`} />

            <Header as='h3' style={{ marginTop: '24px' }}>{i18n.language === 'zh' ? '响应示例' : 'Response Example'}</Header>
            <CodeBlock language='json' code={`{
  "id": "chatcmpl-123",
  "object": "chat.completion",
  "created": 1234567890,
  "model": "gpt-4o",
  "choices": [{
    "index": 0,
    "message": {
      "role": "assistant",
      "content": "Hello! How can I help you today?"
    },
    "finish_reason": "stop"
  }],
  "usage": {
    "prompt_tokens": 20,
    "completion_tokens": 10,
    "total_tokens": 30
  }
}`} />
          </div>
        );

      case 'chat-gemini':
        return (
          <div>
            <Header as='h2' style={{ color: '#1a1a2e', marginBottom: '16px' }}>
              <i className="comment icon" style={{ color: '#667eea' }} />
              {i18n.language === 'zh' ? 'Gemini 对话接口' : 'Gemini Chat API'}
            </Header>
            <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.8', marginBottom: '24px' }}>
              {i18n.language === 'zh' ? '通过 OpenAI 兼容接口调用 Google Gemini 系列模型。支持超长上下文、视觉理解等功能。' : 'Call Google Gemini series models through OpenAI-compatible interface. Supports ultra-long context, vision understanding and more.'}
            </p>

            <Segment style={{ background: '#f0f9ff', borderLeft: '4px solid #0ea5e9' }}>
              <Header as='h4' style={{ color: '#0ea5e9', marginTop: 0 }}>
                <i className="info circle icon" /> {i18n.language === 'zh' ? '支持模型' : 'Supported Models'}
              </Header>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                {['gemini-2.5-flash', 'gemini-2.5-pro', 'gemini-3-flash-preview', 'gemini-3.1-pro-preview'].map(model => (
                  <Label key={model} color='green' size='small'>{model}</Label>
                ))}
              </div>
            </Segment>

            <Header as='h3' style={{ marginTop: '24px' }}>{i18n.language === 'zh' ? '请求示例' : 'Request Example'}</Header>
            <CodeBlock language='bash' code={`curl ${baseUrl}/v1/chat/completions \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "gemini-2.5-flash",
    "messages": [
      {"role": "user", "content": "Explain quantum computing in simple terms"}
    ],
    "max_tokens": 8192
  }'`} />
          </div>
        );

      case 'chat-claude':
        return (
          <div>
            <Header as='h2' style={{ color: '#1a1a2e', marginBottom: '16px' }}>
              <i className="comment icon" style={{ color: '#667eea' }} />
              {i18n.language === 'zh' ? 'Claude 对话接口' : 'Claude Chat API'}
            </Header>
            <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.8', marginBottom: '24px' }}>
              {i18n.language === 'zh' ? '通过 OpenAI 兼容接口调用 Anthropic Claude 系列模型。支持超长上下文、复杂推理、代码生成等功能。' : 'Call Anthropic Claude series models through OpenAI-compatible interface. Supports ultra-long context, complex reasoning, code generation and more.'}
            </p>

            <Segment style={{ background: '#f0f9ff', borderLeft: '4px solid #0ea5e9' }}>
              <Header as='h4' style={{ color: '#0ea5e9', marginTop: 0 }}>
                <i className="info circle icon" /> {i18n.language === 'zh' ? '支持模型' : 'Supported Models'}
              </Header>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                {['claude-sonnet-4-20250514', 'claude-opus-4-20250514', 'claude-haiku-4-5-20251001'].map(model => (
                  <Label key={model} color='orange' size='small'>{model}</Label>
                ))}
              </div>
            </Segment>

            <Header as='h3' style={{ marginTop: '24px' }}>{i18n.language === 'zh' ? '请求示例' : 'Request Example'}</Header>
            <CodeBlock language='bash' code={`curl ${baseUrl}/v1/chat/completions \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "claude-sonnet-4-20250514",
    "messages": [
      {"role": "user", "content": "Write a Python function to sort a list"}
    ],
    "max_tokens": 8192
  }'`} />
          </div>
        );

      case 'chat-ali':
        return (
          <div>
            <Header as='h2' style={{ color: '#1a1a2e', marginBottom: '16px' }}>
              <i className="comment icon" style={{ color: '#667eea' }} />
              {i18n.language === 'zh' ? '阿里云百炼对话接口' : 'Alibaba Cloud Bailian Chat API'}
            </Header>
            <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.8', marginBottom: '24px' }}>
              {i18n.language === 'zh' ? '通过 OpenAI 兼容接口调用阿里云百炼平台的通义千问系列模型。' : 'Call Tongyi Qianwen series models from Alibaba Cloud Bailian platform through OpenAI-compatible interface.'}
            </p>

            <Segment style={{ background: '#f0f9ff', borderLeft: '4px solid #0ea5e9' }}>
              <Header as='h4' style={{ color: '#0ea5e9', marginTop: 0 }}>
                <i className="info circle icon" /> {i18n.language === 'zh' ? '支持模型' : 'Supported Models'}
              </Header>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                {['qwen-turbo', 'qwen-plus', 'qwen-max', 'qwen-vl-max', 'deepseek-r1'].map(model => (
                  <Label key={model} color='purple' size='small'>{model}</Label>
                ))}
              </div>
            </Segment>

            <Header as='h3' style={{ marginTop: '24px' }}>{i18n.language === 'zh' ? '请求示例' : 'Request Example'}</Header>
            <CodeBlock language='bash' code={`curl ${baseUrl}/v1/chat/completions \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "qwen-plus",
    "messages": [
      {"role": "user", "content": "Hello, please introduce yourself"}
    ]
  }'`} />
          </div>
        );

      case 'image-junyun':
        return (
          <div>
            <Header as='h2' style={{ color: '#1a1a2e', marginBottom: '16px' }}>
              <i className="picture icon" style={{ color: '#667eea' }} />
              {i18n.language === 'zh' ? '君云AIGC 图片生成' : 'Junyun AIGC Image Generation'}
            </Header>
            <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.8', marginBottom: '24px' }}>
              {i18n.language === 'zh' ? '通过 OpenAI 兼容的图片生成接口调用君云AIGC服务，支持多种尺寸和风格的图片生成。' : 'Call Junyun AIGC service through OpenAI-compatible image generation interface, supporting various sizes and styles.'}
            </p>

            <Segment style={{ background: '#f0f9ff', borderLeft: '4px solid #0ea5e9' }}>
              <Header as='h4' style={{ color: '#0ea5e9', marginTop: 0 }}>
                <i className="info circle icon" /> {i18n.language === 'zh' ? '支持模型' : 'Supported Models'}
              </Header>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                {['junyun-image', 'junyun-image-gg25'].map(model => (
                  <Label key={model} color='blue' size='small'>{model}</Label>
                ))}
              </div>
            </Segment>

            <Header as='h3' style={{ marginTop: '24px' }}>{i18n.language === 'zh' ? '请求示例' : 'Request Example'}</Header>
            <CodeBlock language='bash' code={`curl ${baseUrl}/v1/images/generations \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "junyun-image",
    "prompt": "A cute orange cat sleeping in the sunshine, anime style",
    "size": "1024x1024",
    "n": 1
  }'`} />

            <Header as='h3' style={{ marginTop: '24px' }}>{i18n.language === 'zh' ? '支持尺寸' : 'Supported Sizes'}</Header>
            <Table celled compact>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{i18n.language === 'zh' ? '尺寸' : 'Size'}</Table.HeaderCell>
                  <Table.HeaderCell>{i18n.language === 'zh' ? '说明' : 'Description'}</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                <Table.Row>
                  <Table.Cell><code>1024x1024</code></Table.Cell>
                  <Table.Cell>{i18n.language === 'zh' ? '1:1 正方形' : '1:1 Square'}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell><code>1024x768</code></Table.Cell>
                  <Table.Cell>{i18n.language === 'zh' ? '4:3 横版' : '4:3 Landscape'}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell><code>768x1024</code></Table.Cell>
                  <Table.Cell>{i18n.language === 'zh' ? '3:4 竖版' : '3:4 Portrait'}</Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
          </div>
        );

      case 'image-ali':
        return (
          <div>
            <Header as='h2' style={{ color: '#1a1a2e', marginBottom: '16px' }}>
              <i className="picture icon" style={{ color: '#667eea' }} />
              {i18n.language === 'zh' ? '阿里通义万相 图片生成' : 'Alibaba Tongyi Wanxiang Image Generation'}
            </Header>
            <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.8', marginBottom: '24px' }}>
              {i18n.language === 'zh' ? '通过 OpenAI 兼容的图片生成接口调用阿里通义万相服务。' : 'Call Alibaba Tongyi Wanxiang service through OpenAI-compatible image generation interface.'}
            </p>

            <Segment style={{ background: '#f0f9ff', borderLeft: '4px solid #0ea5e9' }}>
              <Header as='h4' style={{ color: '#0ea5e9', marginTop: 0 }}>
                <i className="info circle icon" /> {i18n.language === 'zh' ? '支持模型' : 'Supported Models'}
              </Header>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                <Label color='purple' size='small'>wanx-v1</Label>
              </div>
            </Segment>

            <Header as='h3' style={{ marginTop: '24px' }}>{i18n.language === 'zh' ? '请求示例' : 'Request Example'}</Header>
            <CodeBlock language='bash' code={`curl ${baseUrl}/v1/images/generations \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "wanx-v1",
    "prompt": "一只可爱的橘猫在阳光下睡觉",
    "n": 1,
    "size": "1024*1024"
  }'`} />
          </div>
        );

      case 'image-gpt':
        return (
          <div>
            <Header as='h2' style={{ color: '#1a1a2e', marginBottom: '16px' }}>
              <i className="picture icon" style={{ color: '#667eea' }} />
              {i18n.language === 'zh' ? 'GPT Image 图片生成' : 'GPT Image Generation'}
            </Header>
            <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.8', marginBottom: '24px' }}>
              {i18n.language === 'zh' ? '通过 OpenAI 官方的图片生成接口调用 GPT Image 模型。' : 'Call GPT Image models through OpenAI official image generation interface.'}
            </p>

            <Segment style={{ background: '#f0f9ff', borderLeft: '4px solid #0ea5e9' }}>
              <Header as='h4' style={{ color: '#0ea5e9', marginTop: 0 }}>
                <i className="info circle icon" /> {i18n.language === 'zh' ? '支持模型' : 'Supported Models'}
              </Header>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                {['gpt-image-1', 'gpt-image-1.5', 'gpt-image-2'].map(model => (
                  <Label key={model} color='green' size='small'>{model}</Label>
                ))}
              </div>
            </Segment>

            <Header as='h3' style={{ marginTop: '24px' }}>{i18n.language === 'zh' ? '请求示例' : 'Request Example'}</Header>
            <CodeBlock language='bash' code={`curl ${baseUrl}/v1/images/generations \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "gpt-image-2",
    "prompt": "A futuristic city skyline at sunset",
    "n": 1,
    "size": "1024x1024"
  }'`} />
          </div>
        );

      case 'video-kling':
        return (
          <div>
            <Header as='h2' style={{ color: '#1a1a2e', marginBottom: '16px' }}>
              <i className="film icon" style={{ color: '#667eea' }} />
              {i18n.language === 'zh' ? '可灵 Kling 视频生成' : 'Kling Video Generation'}
            </Header>
            <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.8', marginBottom: '24px' }}>
              {i18n.language === 'zh' ? '通过君云AIGC渠道调用快手可灵视频生成模型，支持文生视频功能。' : 'Call Kuaishou Kling video generation model through Junyun AIGC channel, supporting text-to-video.'}
            </p>

            <Segment style={{ background: '#f0f9ff', borderLeft: '4px solid #0ea5e9' }}>
              <Header as='h4' style={{ color: '#0ea5e9', marginTop: 0 }}>
                <i className="info circle icon" /> {i18n.language === 'zh' ? '支持模型' : 'Supported Models'}
              </Header>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                {['junyun-video-kling30', 'junyun-video-kling25', 'junyun-video-klingo1'].map(model => (
                  <Label key={model} color='blue' size='small'>{model}</Label>
                ))}
              </div>
            </Segment>

            <Header as='h3' style={{ marginTop: '24px' }}>{i18n.language === 'zh' ? '请求示例' : 'Request Example'}</Header>
            <CodeBlock language='bash' code={`curl ${baseUrl}/v1/chat/completions \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "junyun-video-kling30",
    "messages": [
      {"role": "user", "content": "Generate a short video of a beautiful sunset over the ocean"}
    ],
    "extra_body": {
      "aspect_ratio": "16:9",
      "duration": "5"
    }
  }'`} />

            <Message style={{ marginTop: '24px', background: '#fff3cd' }}>
              <Message.Header>
                <i className="warning icon" /> {i18n.language === 'zh' ? '注意' : 'Notice'}
              </Message.Header>
              <p>{i18n.language === 'zh' ? '视频生成是异步任务，需要通过任务查询接口获取结果。' : 'Video generation is an asynchronous task. You need to query results through the task query API.'}</p>
            </Message>
          </div>
        );

      case 'video-seedance':
        return (
          <div>
            <Header as='h2' style={{ color: '#1a1a2e', marginBottom: '16px' }}>
              <i className="film icon" style={{ color: '#667eea' }} />
              {i18n.language === 'zh' ? '即梦 Seedance 视频生成' : 'Seedance Video Generation'}
            </Header>
            <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.8', marginBottom: '24px' }}>
              {i18n.language === 'zh' ? '通过火山引擎 Seedance 服务生成视频，支持文生视频和图生视频。' : 'Generate videos through Volcengine Seedance service, supporting text-to-video and image-to-video.'}
            </p>

            <Segment style={{ background: '#f0f9ff', borderLeft: '4px solid #0ea5e9' }}>
              <Header as='h4' style={{ color: '#0ea5e9', marginTop: 0 }}>
                <i className="info circle icon" /> {i18n.language === 'zh' ? '支持模型' : 'Supported Models'}
              </Header>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                <Label color='orange' size='small'>doubao-seedance-2-0-260128</Label>
              </div>
            </Segment>

            <Header as='h3' style={{ marginTop: '24px' }}>{i18n.language === 'zh' ? '文生视频示例' : 'Text-to-Video Example'}</Header>
            <CodeBlock language='bash' code={`curl ${baseUrl}/v1/images/generations \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "doubao-seedance-2-0-260128",
    "prompt": "Generate an elegant animation of cherry blossoms falling",
    "size": "1280x720"
  }'`} />

            <Header as='h3' style={{ marginTop: '24px' }}>{i18n.language === 'zh' ? '图生视频示例' : 'Image-to-Video Example'}</Header>
            <CodeBlock language='bash' code={`curl ${baseUrl}/v1/chat/completions \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "doubao-seedance-2-0-260128",
    "messages": [{
      "role": "user",
      "content": [
        {"type": "text", "text": "Make this scene come alive"},
        {"type": "image_url", "image_url": {"url": "${baseUrl}/uploads/2026/04/21/xxx.jpg"}}
      ]
    }]
  }'`} />
          </div>
        );

      case 'video-hailuo':
        return (
          <div>
            <Header as='h2' style={{ color: '#1a1a2e', marginBottom: '16px' }}>
              <i className="film icon" style={{ color: '#667eea' }} />
              {i18n.language === 'zh' ? '海螺 Hailuo 视频生成' : 'Hailuo Video Generation'}
            </Header>
            <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.8', marginBottom: '24px' }}>
              {i18n.language === 'zh' ? '通过君云AIGC渠道调用海螺视频生成模型。' : 'Call Hailuo video generation model through Junyun AIGC channel.'}
            </p>

            <Segment style={{ background: '#f0f9ff', borderLeft: '4px solid #0ea5e9' }}>
              <Header as='h4' style={{ color: '#0ea5e9', marginTop: 0 }}>
                <i className="info circle icon" /> {i18n.language === 'zh' ? '支持模型' : 'Supported Models'}
              </Header>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                {['junyun-video-hailuo23', 'junyun-video-hailuo23fast'].map(model => (
                  <Label key={model} color='teal' size='small'>{model}</Label>
                ))}
              </div>
            </Segment>

            <Header as='h3' style={{ marginTop: '24px' }}>{i18n.language === 'zh' ? '请求示例' : 'Request Example'}</Header>
            <CodeBlock language='bash' code={`curl ${baseUrl}/v1/chat/completions \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "junyun-video-hailuo23",
    "messages": [
      {"role": "user", "content": "Generate an animation of a kitten playing with yarn"}
    ]
  }'`} />
          </div>
        );

      case 'video-vidu':
        return (
          <div>
            <Header as='h2' style={{ color: '#1a1a2e', marginBottom: '16px' }}>
              <i className="film icon" style={{ color: '#667eea' }} />
              {i18n.language === 'zh' ? '迹兔 Vidu 视频生成' : 'Vidu Video Generation'}
            </Header>
            <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.8', marginBottom: '24px' }}>
              {i18n.language === 'zh' ? '通过君云AIGC渠道调用迹兔视频生成模型。' : 'Call Vidu video generation model through Junyun AIGC channel.'}
            </p>

            <Segment style={{ background: '#f0f9ff', borderLeft: '4px solid #0ea5e9' }}>
              <Header as='h4' style={{ color: '#0ea5e9', marginTop: 0 }}>
                <i className="info circle icon" /> {i18n.language === 'zh' ? '支持模型' : 'Supported Models'}
              </Header>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                {['junyun-video-viduq2pro', 'junyun-video-viduq3pro'].map(model => (
                  <Label key={model} color='purple' size='small'>{model}</Label>
                ))}
              </div>
            </Segment>

            <Header as='h3' style={{ marginTop: '24px' }}>{i18n.language === 'zh' ? '请求示例' : 'Request Example'}</Header>
            <CodeBlock language='bash' code={`curl ${baseUrl}/v1/chat/completions \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "junyun-video-viduq3pro",
    "messages": [
      {"role": "user", "content": "Generate a video of an astronaut walking in space"}
    ]
  }'`} />
          </div>
        );

      case 'video-jimeng':
        return (
          <div>
            <Header as='h2' style={{ color: '#1a1a2e', marginBottom: '16px' }}>
              <i className="film icon" style={{ color: '#667eea' }} />
              {i18n.language === 'zh' ? '即梦 Jimeng 视频生成' : 'Jimeng Video Generation'}
            </Header>
            <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.8', marginBottom: '24px' }}>
              {i18n.language === 'zh' ? '通过君云AIGC渠道调用即梦视频生成模型。' : 'Call Jimeng video generation model through Junyun AIGC channel.'}
            </p>

            <Segment style={{ background: '#f0f9ff', borderLeft: '4px solid #0ea5e9' }}>
              <Header as='h4' style={{ color: '#0ea5e9', marginTop: 0 }}>
                <i className="info circle icon" /> {i18n.language === 'zh' ? '支持模型' : 'Supported Models'}
              </Header>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                <Label color='pink' size='small'>junyun-video-jimeng</Label>
              </div>
            </Segment>

            <Header as='h3' style={{ marginTop: '24px' }}>{i18n.language === 'zh' ? '请求示例' : 'Request Example'}</Header>
            <CodeBlock language='bash' code={`curl ${baseUrl}/v1/chat/completions \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "junyun-video-jimeng",
    "messages": [
      {"role": "user", "content": "Generate a video of a cute cartoon cat running on the grass"}
    ]
  }'`} />
          </div>
        );

      case '3d-text':
        return (
          <div>
            <Header as='h2' style={{ color: '#1a1a2e', marginBottom: '16px' }}>
              <i className="cube icon" style={{ color: '#667eea' }} />
              {i18n.language === 'zh' ? '文生3D' : 'Text to 3D'}
            </Header>
            <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.8', marginBottom: '24px' }}>
              {i18n.language === 'zh' ? '使用腾讯混元3D模型，通过文字描述生成3D资产。' : 'Use Tencent Hunyuan 3D model to generate 3D assets from text descriptions.'}
            </p>

            <Segment style={{ background: '#f0f9ff', borderLeft: '4px solid #0ea5e9' }}>
              <Header as='h4' style={{ color: '#0ea5e9', marginTop: 0 }}>
                <i className="info circle icon" /> {i18n.language === 'zh' ? '支持模型' : 'Supported Models'}
              </Header>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                {['hy-3d-3.0', 'hy-3d-3.1', 'hy-3d-express', 'hy3-preview'].map(model => (
                  <Label key={model} color='purple' size='small'>{model}</Label>
                ))}
              </div>
            </Segment>

            <Header as='h3' style={{ marginTop: '24px' }}>{i18n.language === 'zh' ? '请求示例' : 'Request Example'}</Header>
            <CodeBlock language='bash' code={`curl ${baseUrl}/v1/chat/completions \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "hy-3d-3.1",
    "prompt": "A cute cartoon cat"
  }'`} />
          </div>
        );

      case '3d-image':
        return (
          <div>
            <Header as='h2' style={{ color: '#1a1a2e', marginBottom: '16px' }}>
              <i className="cube icon" style={{ color: '#667eea' }} />
              {i18n.language === 'zh' ? '图生3D' : 'Image to 3D'}
            </Header>
            <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.8', marginBottom: '24px' }}>
              {i18n.language === 'zh' ? '使用腾讯混元3D模型，通过单张图片生成3D资产。' : 'Use Tencent Hunyuan 3D model to generate 3D assets from a single image.'}
            </p>

            <Header as='h3' style={{ marginTop: '24px' }}>{i18n.language === 'zh' ? '请求示例' : 'Request Example'}</Header>
            <CodeBlock language='bash' code={`curl ${baseUrl}/v1/chat/completions \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "hy-3d-3.1",
    "image_url": "https://example.com/input.png"
  }'`} />

            <Message style={{ marginTop: '24px', background: '#fff3cd' }}>
              <Message.Header>
                <i className="warning icon" /> {i18n.language === 'zh' ? '注意' : 'Notice'}
              </Message.Header>
              <p>{i18n.language === 'zh' ? '使用 ' : 'When using '}<code>image_url</code>{i18n.language === 'zh' ? ' 参数时，不要同时传入 ' : ' parameter, do not pass '}<code>prompt</code>{i18n.language === 'zh' ? ' 参数。' : ' parameter at the same time.'}</p>
            </Message>
          </div>
        );

      case '3d-multi':
        return (
          <div>
            <Header as='h2' style={{ color: '#1a1a2e', marginBottom: '16px' }}>
              <i className="cubes icon" style={{ color: '#667eea' }} />
              {i18n.language === 'zh' ? '多图生3D' : 'Multi-Image to 3D'}
            </Header>
            <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.8', marginBottom: '24px' }}>
              {i18n.language === 'zh' ? '使用腾讯混元3D模型（仅 hy-3d-3.0 / hy-3d-3.1 支持），通过多视角图片生成更精确的3D资产。' : 'Use Tencent Hunyuan 3D model (only hy-3d-3.0 / hy-3d-3.1 supported) to generate more accurate 3D assets from multi-view images.'}
            </p>

            <Header as='h3' style={{ marginTop: '24px' }}>{i18n.language === 'zh' ? '请求示例' : 'Request Example'}</Header>
            <CodeBlock language='bash' code={`curl ${baseUrl}/v1/chat/completions \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "hy-3d-3.1",
    "image_url": "https://example.com/front.png",
    "multi_view_images": [
      {"view_type": "left", "view_image_url": "https://example.com/left.png"},
      {"view_type": "right", "view_image_url": "https://example.com/right.png"},
      {"view_type": "back", "view_image_url": "https://example.com/back.png"}
    ]
  }'`} />

            <Segment style={{ marginTop: '24px', background: '#e3f2fd', borderLeft: '4px solid #2196f3' }}>
              <Header as='h4' style={{ color: '#1565c0', marginTop: 0 }}>
                <i className="info circle icon" /> 支持的视角类型
              </Header>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                {['left', 'right', 'back', 'top', 'bottom'].map(type => (
                  <Label key={type} color='blue' size='small'>{type}</Label>
                ))}
              </div>
            </Segment>
          </div>
        );

      case 'task-status':
        return (
          <div>
            <Header as='h2' style={{ color: '#1a1a2e', marginBottom: '16px' }}>
              <i className="check circle icon" style={{ color: '#667eea' }} />
              {i18n.language === 'zh' ? '查询任务状态' : 'Query Task Status'}
            </Header>
            <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.8', marginBottom: '24px' }}>
              {i18n.language === 'zh' ? '通过任务ID查询AIGC任务的执行状态和结果。支持视频生成、3D生成等异步任务。' : 'Query AIGC task execution status and results by task ID. Supports async tasks like video generation, 3D generation, etc.'}
            </p>

            <Header as='h3' style={{ marginTop: '24px' }}>{i18n.language === 'zh' ? '请求示例' : 'Request Example'}</Header>
            <CodeBlock language='bash' code={`curl ${baseUrl}/v1/aigc/task/TASK_ID \\
  -H "Authorization: Bearer ${token}"`} />

            <Header as='h3' style={{ marginTop: '24px' }}>{i18n.language === 'zh' ? '响应示例' : 'Response Example'}</Header>
            <CodeBlock language='json' code={`{
  "task_id": "task-123456",
  "status": "PROCESSING",
  "progress": 65,
  "model_name": "hy-3d-3.1",
  "created_at": 1234567890
}`} />

            <Segment style={{ marginTop: '24px', background: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
              <Header as='h4' style={{ color: '#2e7d32', marginTop: 0 }}>
                <i className="info circle icon" /> {i18n.language === 'zh' ? '任务状态说明' : 'Task Status Description'}
              </Header>
              <Table compact definition>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell width={3}><code>PENDING</code></Table.Cell>
                    <Table.Cell>{i18n.language === 'zh' ? '排队中' : 'Pending'}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell width={3}><code>PROCESSING</code></Table.Cell>
                    <Table.Cell>{i18n.language === 'zh' ? '处理中' : 'Processing'}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell width={3}><code>SUCCESS</code></Table.Cell>
                    <Table.Cell>{i18n.language === 'zh' ? '成功完成' : 'Success'}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell width={3}><code>FAILED</code></Table.Cell>
                    <Table.Cell>{i18n.language === 'zh' ? '失败' : 'Failed'}</Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </Segment>
          </div>
        );

      case 'task-poll':
        return (
          <div>
            <Header as='h2' style={{ color: '#1a1a2e', marginBottom: '16px' }}>
              <i className="refresh icon" style={{ color: '#667eea' }} />
              {i18n.language === 'zh' ? '轮询最佳实践' : 'Polling Best Practices'}
            </Header>
            <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.8', marginBottom: '24px' }}>
              {i18n.language === 'zh' ? 'AIGC任务（视频/3D生成）是异步执行的，需要定期轮询任务状态以获取结果。' : 'AIGC tasks (video/3D generation) are executed asynchronously. You need to poll task status regularly to get results.'}
            </p>

            <Segment style={{ background: '#f0f9ff', borderLeft: '4px solid #0ea5e9' }}>
              <Header as='h4' style={{ color: '#0ea5e9', marginTop: 0 }}>
                <i className="lightbulb icon" /> {i18n.language === 'zh' ? '建议策略' : 'Recommended Strategy'}
              </Header>
              <ul style={{ marginLeft: '20px', lineHeight: '2' }}>
                <li>{i18n.language === 'zh' ? '每 ' : 'Poll every '}<strong>3-5 {i18n.language === 'zh' ? '秒' : 'seconds'}</strong>{i18n.language === 'zh' ? ' 轮询一次任务状态' : ''}</li>
                <li>{i18n.language === 'zh' ? '视频生成通常需要 ' : 'Video generation usually takes '}<strong>30-60 {i18n.language === 'zh' ? '秒' : 'seconds'}</strong></li>
                <li>{i18n.language === 'zh' ? '3D生成通常需要 ' : '3D generation usually takes '}<strong>60-120 {i18n.language === 'zh' ? '秒' : 'seconds'}</strong></li>
                <li>{i18n.language === 'zh' ? '任务完成后立即停止轮询' : 'Stop polling immediately after task completion'}</li>
                <li>{i18n.language === 'zh' ? '设置最大轮询次数避免无限循环（建议 100 次）' : 'Set maximum polling attempts to avoid infinite loops (recommended: 100)'}</li>
              </ul>
            </Segment>

            <Header as='h3' style={{ marginTop: '24px' }}>{i18n.language === 'zh' ? '轮询代码示例 (JavaScript)' : 'Polling Code Example (JavaScript)'}</Header>
            <CodeBlock language='javascript' code={`async function pollTaskStatus(taskId, apiKey) {
  const maxAttempts = 100;
  const interval = 5000; // 5秒
  
  for (let i = 0; i < maxAttempts; i++) {
    const response = await fetch(\`\${baseUrl}/v1/aigc/task/\${taskId}\`, {
      headers: {
        'Authorization': \`Bearer \${apiKey}\`
      }
    });
    
    const result = await response.json();
    
    if (result.status === 'SUCCESS') {
      console.log('Task completed:', result);
      return result;
    }
    
    if (result.status === 'FAILED') {
      throw new Error('Task failed');
    }
    
    console.log(\`Progress: \${result.progress}%\`);
    await new Promise(resolve => setTimeout(resolve, interval));
  }
  
  throw new Error('Task timeout');
}`} />
          </div>
        );
        return (
          <div>
            <Header as='h2' style={{ color: '#1a1a2e' }}>
              {i18n.language === 'zh' ? '任务查询' : 'Task Query'}
            </Header>
            <p style={{ color: '#555', lineHeight: '1.8' }}>
              {i18n.language === 'zh'
                ? '异步任务（视频生成、3D生成）创建后，可使用此接口查询任务状态。'
                : 'After creating async tasks (video generation, 3D generation), use this endpoint to query task status.'}
            </p>

            <Segment style={{ marginTop: '20px', background: '#fff8e1', borderLeft: '4px solid #ffc107' }}>
              <Header as='h4' style={{ color: '#f57f17' }}>
                <i className="search icon" /> GET /v1/aigc/task/:taskId
              </Header>
            </Segment>

            <CodeBlock language='bash' code={`# 查询任务状态
curl ${baseUrl}/v1/aigc/task/TASK_ID \\
  -H "Authorization: Bearer ${token}"`} />

            <Header as='h3' style={{ marginTop: '24px' }}>
              {i18n.language === 'zh' ? '响应示例' : 'Response Example'}
            </Header>
            <CodeBlock language='json' code={`{
  "task_id": "task-123456",
  "status": "PROCESSING",
  "progress": 65,
  "model_name": "hy-3d-3.1",
  "created_at": 1234567890
}`} />

            <Message style={{ marginTop: '24px', background: '#e3f2fd' }}>
              <Message.Header>
                <i className="info circle icon" /> {i18n.language === 'zh' ? '提示' : 'Tip'}
              </Message.Header>
              <p>
                {i18n.language === 'zh'
                  ? '建议每 3-5 秒轮询一次任务状态，通常视频/3D生成需要 30-60 秒。'
                  : 'Recommended to poll task status every 3-5 seconds. Video/3D generation usually takes 30-60 seconds.'}
              </p>
            </Message>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      {/* 顶部导航栏 */}
      <div style={{
        background: '#fff',
        borderBottom: '1px solid #e8e8e8',
        padding: '0 24px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <Menu pointing secondary style={{ border: 'none', marginBottom: 0 }}>
            <Menu.Item>
              <Header as='h2' style={{ margin: 0, color: '#1a1a2e', fontSize: '20px' }}>
                <i className="book icon" style={{ color: '#667eea' }} />
                {i18n.language === 'zh' ? 'API 文档' : 'API Documentation'}
              </Header>
            </Menu.Item>
          </Menu>
        </div>
      </div>

      <Container style={{ maxWidth: '1400px', padding: '24px' }}>
        <Grid>
          {/* 左侧导航 */}
          <Grid.Column width={4}>
            <div style={{ position: 'sticky', top: '80px' }}>
              <Segment basic style={{ padding: 0 }}>
              {/* 二级导航菜单 */}
              <div style={{ padding: '8px' }}>
                {menuStructure.map(section => {
                  const isExpanded = expandedSections.includes(section.key);
                  const hasActiveChild = section.children.some(child => child.key === activeSection);
                  
                  return (
                    <div key={section.key} style={{ marginBottom: '8px' }}>
                      {/* 一级菜单 */}
                      <div
                        onClick={() => toggleSection(section.key)}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          background: hasActiveChild ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                          color: hasActiveChild ? '#fff' : '#333',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          fontWeight: 'bold',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <i className={`${section.icon} icon`} style={{ fontSize: '16px' }} />
                          <span>{section.title}</span>
                        </div>
                        <i className={`${isExpanded ? 'angle down' : 'angle right'} icon`} />
                      </div>
                      
                      {/* 二级菜单 */}
                      {isExpanded && (
                        <div style={{ paddingLeft: '24px', marginTop: '8px' }}>
                          {section.children.map(child => (
                            <div
                              key={child.key}
                              onClick={() => setActiveSection(child.key)}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                background: activeSection === child.key ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
                                color: activeSection === child.key ? '#667eea' : '#666',
                                borderRadius: '6px',
                                marginBottom: '2px',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <i className={`${child.icon} icon`} style={{ fontSize: '12px' }} />
                              <span>{child.text}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              </Segment>
            </div>
          </Grid.Column>

          {/* 右侧内容区 */}
          <Grid.Column width={12}>
            <Segment style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '32px',
              minHeight: '600px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            }}>
              {renderContent()}
            </Segment>
          </Grid.Column>
        </Grid>
      </Container>
    </div>
  );
};

export default APIDoc;
